import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, object = logged
  const [error, setError] = useState("");

  const bootstrap = useCallback(async () => {
    // If returning from Emergent OAuth, let AuthCallback handle it first
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      return;
    }
    const token = localStorage.getItem("access_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("user", JSON.stringify(data));
    } catch {
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      setUser(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("user", JSON.stringify(data.user));
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  };

  const loginWithGoogleSession = async (sessionId) => {
    setError("");
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, loginWithGoogleSession, logout, refresh: bootstrap }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export const ROLE_LABEL = {
  super_admin: "Super Admin",
  branch_admin: "Branch Admin",
  agent: "Agent",
  staff: "Staff",
};

export const can = (user, resource, action, matrix) => {
  if (!user || !matrix) return false;
  return (matrix[resource]?.[action] || []).includes(user.role);
};
